import { QueryBusinessDataResult } from "@/api/business_gmv/interface";
import { EChartOption, EChartsResponsiveOption } from "echarts";
import echarts from "echarts";
import React, { useEffect, useMemo, useRef } from "react";
import { renderBigNumber, renderNumber, renderPrice } from "@/common/render";
import moment from "moment";
import {
  SELECTED_CARD_LIST_KEY_MAP,
  SELECTED_CARD_TITLE_MAP,
} from "views/menu_layout/gmv_overview/util";
import { CardItem } from "../common";

interface Props {
  selectedCard?: CardItem;
  businessData?: QueryBusinessDataResult;
  title?: string;
  hideLegend?: boolean;
  isPrice?: boolean;
}

const Chart = ({ selectedCard = {}, businessData, hideLegend }: Props) => {
  const chartInstance = useRef<echarts.ECharts>();

  const charData = useMemo(() => {
    if (selectedCard && businessData) {
      return (
        businessData.gmvInfoList?.map((item) => ({
          x: item.payTime,
          y: item[SELECTED_CARD_LIST_KEY_MAP[selectedCard.key]],
        })) || []
      );
    }

    return [];
  }, [selectedCard, businessData]);

  const renderChartData = (data) => {
    if (selectedCard.isPrice) {
      return renderPrice(data);
    }

    return renderNumber(data);
  };

  useEffect(() => {
    if (chartInstance.current && charData) {
      const option: EChartOption | EChartsResponsiveOption = {
        title: {
          textStyle: {
            color: "rgba(56,60,80,0.95)",
            fontSize: 16,
          },
        },
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            return `
              <div style="width: 233px;height:80px;background:#fff;margin:-10px;box-shadow:0px 2px 8px 0px rgba(56,60,80,0.16);padding:16px;color: #000;border-radius: 4px;">
              ${
                params[0].marker
              }<span style="font-family: PingFangSC-Medium;color: rgba(56,60,80,0.95);">${
              params[0].axisValue
            }</span><br/>
              <div style="font-family: PingFangSC-Regular;margin-top:4px;color: rgba(56,60,80,0.95);">${
                params[0].seriesName
              }: ${renderChartData(params[0].data)}</div>
              </div>
            `;
          },
        },
        legend: hideLegend
          ? {
              show: false,
            }
          : {
              icon: "rect",
              top: "12px",
            },
        color: ["#4c67ff"],
        grid: {
          left: "0px",
          right: "0px",
          bottom: "8px",
          top: hideLegend ? "30px" : "85px",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          boundaryGap: true,
          data: charData.map((item) => moment(item.x).format("YYYY/MM/DD")),
          axisLine: {
            lineStyle: {
              color: "#EBECF2",
            },
          },
          axisTick: {
            alignWithLabel: true,
          },
          axisLabel: {
            color: "rgba(56,60,80,0.65)",
          },
          axisPointer: {
            lineStyle: {
              color: "rgba(56,60,80,0.65)",
            },
          },
        },
        yAxis: {
          type: "value",
          max: "dataMax",
          min: "dataMin",
          splitNumber: 4,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: "rgba(56,60,80,0.65)",
            formatter: (v) => {
              if (selectedCard.isPrice) {
                return renderBigNumber(v, undefined, undefined, true);
              }

              return renderNumber(v);
            },
          },
          splitLine: {
            lineStyle: {
              color: "#EBECF2",
              type: "dashed",
            },
          },
        },
        series: [
          {
            name: SELECTED_CARD_TITLE_MAP[selectedCard.key],
            type: "line",
            data: charData.map((item) =>
              selectedCard.isPrice ? item.y / 100 : item.y
            ),
            smooth: true,
            showSymbol: false,
            symbol: "circle",
          },
        ],
      };
      chartInstance.current.clear();
      chartInstance.current.setOption(option);
    }
  }, [charData]);

  return (
    <div className="relative h-full w-full">
      <div
        className="h-full w-full"
        ref={(dom) => {
          if (dom && !chartInstance.current) {
            chartInstance.current = echarts.init(dom);
          }
        }}
      />
      <div className="title absolute left-0 top-0 text-xs text-text-black/[.65]">
        {selectedCard.isPrice
          ? `${selectedCard.title} (元)`
          : selectedCard.title}
      </div>
    </div>
  );
};

export default Chart;
